import { 
  usePendingInvitations, 
  useAcceptInvitation, 
  useDeclineInvitation 
} from '@/hooks/usePendingInvitations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, AlertTriangle, Users, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const PendingInvitationsDialog = () => {
  const { data: invitations, isLoading } = usePendingInvitations();
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();
  
  if (isLoading || !invitations || invitations.length === 0) {
    return null;
  }
  
  const invitation = invitations[0];
  if (!invitation) return null;

  const handleAccept = () => {
    acceptInvitation.mutate(invitation.id);
  };

  const handleDecline = () => {
    declineInvitation.mutate(invitation.id);
  };

  const isOnlyMember = invitation.current_cabinet.member_count === 1;
  const expiresIn = formatDistanceToNow(new Date(invitation.expires_at), {
    locale: fr,
    addSuffix: true
  });
  
  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleDecline()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 bg-grey-100 rounded-full">
            <Building2 className="h-8 w-8 text-grey-600" />
          </div>
          <DialogTitle className="text-base font-medium text-center">
            Invitation à rejoindre un cabinet
          </DialogTitle>
          <DialogDescription className="text-center">
            Vous avez été invité à rejoindre un nouveau cabinet
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Cabinet cible */}
          <div className="p-4 bg-grey-50 rounded-lg border border-grey-200">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-grey-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-charcoal">
                  {invitation.target_cabinet.name}
                </div>
                <div className="text-sm text-grey-600 flex items-center gap-1 mt-1">
                  <Users className="h-3.5 w-3.5" />
                  {invitation.target_cabinet.member_count} membre(s)
                </div>
                <div className="text-sm text-grey-600 mt-2">
                  Invité par : <span className="font-medium">{invitation.invited_by.name}</span>
                  <br />
                  <span className="text-xs text-grey-500">{invitation.invited_by.email}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-medium mb-1">Attention</div>
              <div className="text-sm space-y-1">
                <div>
                  • Vous quitterez votre cabinet actuel "{invitation.current_cabinet.name}"
                </div>
                {isOnlyMember && (
                  <div className="font-medium">
                    • Votre cabinet sera supprimé car vous êtes le seul membre
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
          
          {/* Expiration */}
          <div className="flex items-center gap-2 text-sm text-grey-600">
            <Clock className="h-4 w-4" />
            <span>Cette invitation expire {expiresIn}</span>
          </div>
          
          {/* Autres invitations */}
          {invitations.length > 1 && (
            <Badge variant="outline" className="w-fit">
              +{invitations.length - 1} autre(s) invitation(s) en attente
            </Badge>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleDecline}
            disabled={acceptInvitation.isPending || declineInvitation.isPending}
          >
            Refuser
          </Button>
          <Button
            onClick={handleAccept}
            disabled={acceptInvitation.isPending || declineInvitation.isPending}
            className="bg-charcoal hover:bg-charcoal-dark"
          >
            {acceptInvitation.isPending ? 'Traitement...' : 'Accepter et rejoindre'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PendingInvitationsDialog;
